import { useState, useCallback, useEffect, useRef } from "react";
import AppLayout from "@/layouts/AppLayout";
import useCapabilities, { useCanAccessPhotoboothBool, useDenyAuthorization } from "@/hooks/useCapabilities";
import useAmplitude from "@/hooks/useAmplitude";
import EntryPage from "./_components/EntryPage";
import CameraPage from "./_components/CameraPage";
import PreviewPage from "./_components/PreviewPage";
import { PhotoboothPerson } from "./queries";
import { registerBgRemovalCacheWorker } from "./useBgRemovalCache";
import {
  PHOTOBOOTH_ANALYTICS_EVENTS,
  getPhotoboothOpenedAnalyticsData,
  type PhotoboothCaptureMethod
} from "./_utils/amplitude";

type Screen = "entry" | "camera" | "preview";

type PhotoboothState = {
  screen: Screen;
  selectedPerson: PhotoboothPerson | null;
  capturedImage: string | null;
  captureMethod: PhotoboothCaptureMethod | null;
};

export default function PhotoboothPage() {
  const { loaded } = useCapabilities();
  const denyAuthorization = useDenyAuthorization();
  const hasAccess = useCanAccessPhotoboothBool();
  const { trackEvent } = useAmplitude();
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (!hasAccess) {
      denyAuthorization();
      return;
    }

    if (!hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackEvent(
        PHOTOBOOTH_ANALYTICS_EVENTS.opened,
        getPhotoboothOpenedAnalyticsData({
          source: "photobooth_page"
        })
      );
    }
  }, [loaded, hasAccess, denyAuthorization, trackEvent]);

  const [state, setState] = useState<PhotoboothState>({
    screen: "entry",
    selectedPerson: null,
    capturedImage: null,
    captureMethod: null
  });

  // Register the background removal model cache service worker
  // This caches the ONNX model files so they don't need to be re-downloaded
  useEffect(() => {
    registerBgRemovalCacheWorker();
  }, []);

  const handleSelectPerson = useCallback((person: PhotoboothPerson) => {
    setState((prev) => ({
      ...prev,
      selectedPerson: person,
      screen: "camera"
    }));
  }, []);

  const handleCapturePhoto = useCallback((imageData: string, captureMethod: PhotoboothCaptureMethod) => {
    setState((prev) => ({
      ...prev,
      capturedImage: imageData,
      captureMethod,
      screen: "preview"
    }));
  }, []);

  const handleRetake = useCallback(() => {
    setState((prev) => ({
      ...prev,
      capturedImage: null,
      captureMethod: null,
      screen: "camera"
    }));
  }, []);

  const handleClose = useCallback(() => {
    setState({
      screen: "entry",
      selectedPerson: null,
      capturedImage: null,
      captureMethod: null
    });
  }, []);

  const handleSaveComplete = useCallback(() => {
    setState({
      screen: "entry",
      selectedPerson: null,
      capturedImage: null,
      captureMethod: null
    });
  }, []);

  if (!loaded || !hasAccess) {
    return null;
  }

  // Camera and Preview pages are full-screen immersive experiences
  if (state.screen === "camera" && state.selectedPerson) {
    return <CameraPage person={state.selectedPerson} onCapture={handleCapturePhoto} onClose={handleClose} />;
  }

  if (state.screen === "preview" && state.selectedPerson && state.capturedImage && state.captureMethod) {
    return (
      <PreviewPage
        person={state.selectedPerson}
        imageData={state.capturedImage}
        captureMethod={state.captureMethod}
        onRetake={handleRetake}
        onClose={handleClose}
        onSaveComplete={handleSaveComplete}
      />
    );
  }

  // Entry page with standard layout
  return (
    <AppLayout>
      <EntryPage onSelectPerson={handleSelectPerson} />
    </AppLayout>
  );
}
